import { Button, Collapse } from 'antd';
import dayjs from 'dayjs';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import {
  InfoBlock,
  InfoBlockRows,
} from 'app/components/commons/InfoBlock/InfoBlock';
import type {
  BookingDetails,
  BookingVoucherResponse,
} from 'app/typings/core/admin/bookings/booking';
import { parseUppercaseSnakeTextToCapitalize } from 'app/utils/string';

const { Panel } = Collapse;

type VouchersProps = {
  bookingDetails: BookingDetails;
  onOpenVoucherModal: (voucher: BookingVoucherResponse) => void;
};

export const Vouchers = ({
  bookingDetails,
  onOpenVoucherModal,
}: VouchersProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const formatVoucherName = (
    voucher: BookingVoucherResponse,
    totalVoucher: number
  ) => {
    return `Vouchers - ${dayjs(voucher.createdAt).format(
      'DD/MM/YYYY'
    )} - ${currencyFormatter(totalVoucher)}`;
  };

  const handleOpenVoucherModal = (
    e: React.MouseEvent<HTMLElement>,
    voucher: BookingVoucherResponse
  ) => {
    e.stopPropagation();
    onOpenVoucherModal(voucher);
  };

  return (
    <InfoBlock title="Vouchers">
      <Collapse>
        {bookingDetails.vouchers.map((voucher) => {
          const header = formatVoucherName(
            voucher,
            Number(voucher.initialValue) ?? 0
          );

          return (
            <Panel
              header={header}
              key={header}
              extra={
                <Button onClick={(e) => handleOpenVoucherModal(e, voucher)}>
                  Update
                </Button>
              }
            >
              <InfoBlockRows
                rows={[
                  {
                    label: 'Code',
                    value: voucher.code,
                  },
                  {
                    label: 'Initial value',
                    value: currencyFormatter(Number(voucher.initialValue) ?? 0),
                  },
                  {
                    label: 'Remaining value',
                    value: currencyFormatter(
                      Number(voucher.remainingValue) ?? 0
                    ),
                  },
                  {
                    label: 'Expiry date',
                    value: voucher.expirationDate
                      ? dayjs(voucher.expirationDate).format('DD/MM/YYYY')
                      : '-',
                  },
                  {
                    label: 'Created at',
                    value: dayjs(voucher.createdAt).format('DD/MM/YYYY'),
                  },
                  {
                    label: 'Created by',
                    value: `${voucher.user.firstName} ${voucher.user.lastName}`,
                  },
                  {
                    label: 'Reason type',
                    value: voucher.reasonType
                      ? parseUppercaseSnakeTextToCapitalize(voucher.reasonType)
                      : '-',
                  },
                  {
                    label: 'Comment',
                    value: voucher.comment ? voucher.comment : '-',
                  },
                ]}
              />
            </Panel>
          );
        })}
      </Collapse>
    </InfoBlock>
  );
};
